/* Licht-Vorschau: berechnet Lightmaps ähnlich hlrad (nur direktes Licht, Schatten per Strahlentest, kein Bounce).
 * Reine Logik ohne DOM. Ergebnis: ein Atlas (RGBA) plus je Fläche ein Eintrag zum Berechnen der UV-Koordinaten. */
import type { Brush, Doc, Entity, Vec3 } from '../model/types.ts';
import Core from '../core.ts';
import I18n from '../i18n.ts';
/** Lichtquelle: Sonne/Himmel (env), Spot oder Punktlicht; col = Farbe 0..1, i = Helligkeit */
export type LightSource =
  | { type: 'env'; dir: Vec3; col: Vec3; i: number; dcol: Vec3; di: number }
  | { type: 'spot'; p: Vec3; dir: Vec3; col: Vec3; i: number; cin: number; cout: number }
  | { type: 'point'; p: Vec3; col: Vec3; i: number };
/** Fläche mit ihrem Ausschnitt im Lightmap-Atlas (x0,y0: Lage, w×h Luxel, W×H mit Rand) */
export interface LightFace {
  b: Brush; fi: number; n: Vec3; U: Vec3; V: Vec3; d: number; s0: number; s1: number; t0: number; t1: number;
  rs: number; w: number; h: number; W: number; H: number; x0: number; y0: number; rad: Float32Array | null; maxd: number;
}
/** Abbildung Weltpunkt → Atlas-UV für eine berechnete Fläche */
export interface FaceUv { x0: number; y0: number; rs: number; s0: number; t0: number; U: Vec3; V: Vec3 }
export interface LightResult {
  W: number; H: number; data: Uint8ClampedArray; map: WeakMap<Brush, FaceUv[]>;
  lights: number; luxels: number; res: number; faces: number; dark: number; ms: number; bounce: boolean;
}
export interface LightOptions { res: number; shadows: boolean; ambient: number; atlas: number; sky: number; bounce: boolean; flipSun: boolean }
/** Berechnung in Zeitscheiben: step(ms) rechnet höchstens ms weiter und liefert true, wenn fertig */
export interface LightJob {
  lights: LightSource[]; faceCount: number; luxels: number; res: number; atlas: number; done: boolean;
  result: LightResult | null; doneLux: number; t0: number; progress: () => number; step: (ms: number) => boolean;
}
interface OccItem { pl: Float64Array; n: number; mn: Vec3; mx: Vec3; c: Vec3 }
interface OccNode { mn: Vec3; mx: Vec3; items: OccItem[] | null; l: OccNode | null; r: OccNode | null }
interface Patch { x: number; y: number; z: number; nx: number; ny: number; nz: number; r: number; g: number; b: number; a: number; w: number }

const Light = (function (Core) {
  'use strict';
  const _ = I18n.t;

  const NONSOLID = /^(SKY|CLIP|AAATRIGGER|ORIGIN|SKIP|HINT|SOLIDHINT|BOUNDINGBOX|NOCLIP|CONTENTWATER|NULL|BEVEL)$/i;
  const NOOCCLUDE_TEX = /^(SKY|CLIP|AAATRIGGER|ORIGIN|SKIP|HINT|SOLIDHINT|BOUNDINGBOX|NOCLIP|CONTENTWATER)$/i;
  const NOOCCLUDE_ENT = /^(trigger_|func_illusionary|func_ladder|func_water|func_conveyor)/;
  const GAMMA = 0.55; // hlrad-Standard (-gamma 0.55)
  const LIGHTSCALE = 2; // hlrad-Standard (-lightscale 2): gilt für Sonne und Himmel; bei Punktlichtern steckt er in KS
  const DEG = Math.PI / 180;
  const KC = 0.178; // empirisch an sdHLRAD (v1.3.0) gemessen: Helligkeit ∝ I³·cos/d² (Bereich I = 50..400)
  const KS = 8192; // an hlrad gemessen: Punktlicht I: Helligkeit (0..255, vor Gamma) = I·KS·cos/d², KS = 4096 × lightscale 2

  const dirFrom = (yaw: number, pitch: number): Vec3 => {
    const cy = Math.cos(yaw * DEG), sy = Math.sin(yaw * DEG), cp = Math.cos(pitch * DEG), sp = Math.sin(pitch * DEG);
    return [cy * cp, sy * cp, sp];
  };

  function parseLight(e: { kv: Record<string, string> }): { col: Vec3; i: number } {
    const a = String(e.kv._light || '255 255 255 200').trim().split(/\s+/).map(Number).filter((x) => !isNaN(x));
    if (a.length >= 4) return { col: [a[0] / 255, a[1] / 255, a[2] / 255], i: a[3] };
    if (a.length === 3) return { col: [a[0] / 255, a[1] / 255, a[2] / 255], i: 200 };
    if (a.length === 1) return { col: [1, 1, 1], i: a[0] };
    return { col: [1, 1, 1], i: 200 };
  }

  /** Alle Lichtquellen der Map. type: point | spot | env */
  function collect(doc: Doc, flipSun?: boolean, only?: Entity | null): LightSource[] {
    const out: LightSource[] = [], names = new Map<string, Entity>();
    doc.ents.forEach((e) => { if (e.kv.targetname) names.set(e.kv.targetname, e); });
    doc.ents.forEach((e) => {
      const cls = e.kv.classname || '';
      if (only && e !== only) return;
      if (!/^light(_spot|_environment)?$/.test(cls)) return;
      const o: Vec3 = Core.parseVec(e.kv.origin) || [0, 0, 0], L = parseLight(e);
      if (L.i <= 0) return;
      const ang = String(e.kv.angles || '0 0 0').trim().split(/\s+/).map(Number);
      let yaw = ang[1] || 0;
      if (e.kv.angle !== undefined && !e.kv.angles) yaw = Number(e.kv.angle) || 0;
      const pitch = e.kv.pitch !== undefined && e.kv.pitch !== '' ? Number(e.kv.pitch) : -(ang[0] || 0);
      if (cls === 'light_environment') {
        const d = dirFrom(yaw, pitch);
        if (flipSun) { d[0] = -d[0]; d[1] = -d[1]; }
        const D = e.kv._diffuse_light !== undefined && String(e.kv._diffuse_light).trim() !== '' ? parseLight({ kv: { _light: e.kv._diffuse_light } }) : L;
        out.push({ type: 'env', dir: d, col: L.col, i: L.i, dcol: D.col, di: D.i });
      } else if (cls === 'light_spot') {
        let d = dirFrom(yaw, pitch);
        const t = e.kv.target && names.get(e.kv.target), to = t && Core.parseVec(t.kv.origin);
        if (to) d = Core.norm(Core.sub(to, o));
        const inner = Number(e.kv._cone) || 30, outer = Math.max(inner + 1, Number(e.kv._cone2) || 45);
        out.push({ type: 'spot', p: o, dir: d, col: L.col, i: L.i, cin: Math.cos(inner * DEG), cout: Math.cos(outer * DEG) });
      } else {
        out.push({ type: 'point', p: o, col: L.col, i: L.i });
      }
    });
    return out;
  }

  // ------------------------------------------------------------ Strahlentest (BVH über Brush-Hüllen)
  function buildOccluders(doc: Doc): { hit: (ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, tmax: number) => boolean; count: number } {
    const items: OccItem[] = [];
    doc.ents.forEach((e, ei) => {
      if (ei > 0 && NOOCCLUDE_ENT.test(e.kv.classname || '')) return;
      e.brushes.forEach((b) => {
        if (b.faces.every((f) => NOOCCLUDE_TEX.test(f.tex))) return;
        const pl = new Float64Array(b.faces.length * 4);
        b.faces.forEach((f, k) => {
          const n = Core.faceNormal(b.verts, f.vi);
          pl[k * 4] = n[0]; pl[k * 4 + 1] = n[1]; pl[k * 4 + 2] = n[2]; pl[k * 4 + 3] = Core.dot(n, b.verts[f.vi[0]]);
        });
        const bb = Core.bbox(b);
        items.push({ pl: pl, n: b.faces.length, mn: bb.min, mx: bb.max, c: [(bb.min[0] + bb.max[0]) / 2, (bb.min[1] + bb.max[1]) / 2, (bb.min[2] + bb.max[2]) / 2] });
      });
    });
    const build = (list: OccItem[]): OccNode => {
      const mn: Vec3 = [Infinity, Infinity, Infinity], mx: Vec3 = [-Infinity, -Infinity, -Infinity];
      list.forEach((it) => { for (let i = 0; i < 3; i++) { if (it.mn[i] < mn[i]) mn[i] = it.mn[i]; if (it.mx[i] > mx[i]) mx[i] = it.mx[i]; } });
      const node: OccNode = { mn: mn, mx: mx, items: null, l: null, r: null };
      if (list.length <= 4) { node.items = list; return node; }
      let ax = 0, best = -1;
      for (let i = 0; i < 3; i++) if (mx[i] - mn[i] > best) { best = mx[i] - mn[i]; ax = i; }
      list.sort((a, b) => a.c[ax] - b.c[ax]);
      const m = list.length >> 1;
      node.l = build(list.slice(0, m)); node.r = build(list.slice(m));
      return node;
    };
    const root = items.length ? build(items) : null;
    const stack: OccNode[] = [];
    /** true, wenn der Strahl o + t·d für 0 < t < tmax ein Brush trifft (von innen startende Brushes zählen nicht). */
    function hit(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, tmax: number): boolean {
      if (!root) return false;
      stack.length = 0; stack.push(root);
      const ix = dx === 0 ? 1e30 : 1 / dx, iy = dy === 0 ? 1e30 : 1 / dy, iz = dz === 0 ? 1e30 : 1 / dz;
      while (stack.length) {
        const nd = stack.pop() as OccNode;
        let t0 = 0, t1 = tmax, a, b, t;
        a = (nd.mn[0] - ox) * ix; b = (nd.mx[0] - ox) * ix; if (a > b) { t = a; a = b; b = t; } if (a > t0) t0 = a; if (b < t1) t1 = b; if (t0 > t1) continue;
        a = (nd.mn[1] - oy) * iy; b = (nd.mx[1] - oy) * iy; if (a > b) { t = a; a = b; b = t; } if (a > t0) t0 = a; if (b < t1) t1 = b; if (t0 > t1) continue;
        a = (nd.mn[2] - oz) * iz; b = (nd.mx[2] - oz) * iz; if (a > b) { t = a; a = b; b = t; } if (a > t0) t0 = a; if (b < t1) t1 = b; if (t0 > t1) continue;
        if (nd.items) {
          for (let k = 0; k < nd.items.length; k++) {
            const it = nd.items[k], pl = it.pl;
            let te = -Infinity, tx = Infinity, miss = false;
            for (let f = 0; f < it.n; f++) {
              const nx = pl[f * 4], ny = pl[f * 4 + 1], nz = pl[f * 4 + 2], d = pl[f * 4 + 3];
              const den = nx * dx + ny * dy + nz * dz, num = d - (nx * ox + ny * oy + nz * oz);
              if (Math.abs(den) < 1e-12) { if (num < 0) { miss = true; break; } continue; }
              const tt = num / den;
              if (den < 0) { if (tt > te) te = tt; } else if (tt < tx) tx = tt;
              if (te > tx) { miss = true; break; }
            }
            if (!miss && te > 0.01 && te < tmax && te <= tx) return true;
          }
        } else { stack.push(nd.l as OccNode); stack.push(nd.r as OccNode); }
      }
      return false;
    }
    return { hit: hit, count: items.length };
  }

  // ------------------------------------------------------------ Flächen und Atlas
  function planeAxes(n: Vec3): { U: Vec3; V: Vec3 } {
    const ax = Math.abs(n[0]), ay = Math.abs(n[1]), az = Math.abs(n[2]);
    const h: Vec3 = az <= ax && az <= ay ? [0, 0, 1] : ay <= ax ? [0, 1, 0] : [1, 0, 0];
    const U = Core.norm(Core.cross(n, h)), V = Core.cross(n, U);
    return { U: U, V: V };
  }

  function collectFaces(doc: Doc): LightFace[] {
    const faces: LightFace[] = [];
    doc.ents.forEach((e, ei) => {
      if (ei > 0 && /^trigger_/.test(e.kv.classname || '')) return;
      e.brushes.forEach((b) => b.faces.forEach((f, fi) => {
        if (NONSOLID.test(f.tex)) return;
        const n = Core.faceNormal(b.verts, f.vi), ax = planeAxes(n);
        let s0 = Infinity, s1 = -Infinity, t0 = Infinity, t1 = -Infinity;
        f.vi.forEach((i) => {
          const p = b.verts[i], s = Core.dot(p, ax.U), t = Core.dot(p, ax.V);
          if (s < s0) s0 = s; if (s > s1) s1 = s; if (t < t0) t0 = t; if (t > t1) t1 = t;
        });
        faces.push({ b: b, fi: fi, n: n, U: ax.U, V: ax.V, d: Core.dot(n, b.verts[f.vi[0]]), s0: s0, s1: s1, t0: t0, t1: t1, rs: 0, w: 0, h: 0, W: 0, H: 0, x0: 0, y0: 0, rad: null, maxd: 0 });
      }));
    });
    return faces;
  }

  /** Verteilt Rechtecke mit Regalpackung. Liefert true, wenn alles in A×A passt. */
  function pack(rects: LightFace[], A: number, startX: number, startY: number, startRow: number): boolean {
    let x = startX, y = startY, row = startRow;
    for (let k = 0; k < rects.length; k++) {
      const r = rects[k];
      if (x + r.W > A) { x = 0; y += row; row = 0; }
      if (y + r.H > A) return false;
      r.x0 = x; r.y0 = y; x += r.W; if (r.H > row) row = r.H;
    }
    return true;
  }

  function layout(faces: LightFace[], res: number, A: number): number {
    let rs = res;
    for (let attempt = 0; attempt < 16; attempt++, rs *= 1.25) {
      faces.forEach((f) => {
        f.rs = Math.max(rs, (f.s1 - f.s0) / 96, (f.t1 - f.t0) / 96);
        f.w = Math.max(2, Math.ceil((f.s1 - f.s0) / f.rs) + 1);
        f.h = Math.max(2, Math.ceil((f.t1 - f.t0) / f.rs) + 1);
        f.W = f.w + 2; f.H = f.h + 2;
      });
      const order = faces.slice().sort((a, b) => b.H - a.H || b.W - a.W);
      if (pack(order, A, 4, 0, 4)) return rs; // 4 px breit für den weißen Block links oben
    }
    return -1;
  }

  /** Reichweite (Einheiten), bis die Helligkeit eines Lichts auf etwa 10 % gefallen ist. */
  const range = (intensity: number): number => Math.sqrt(KC * intensity * intensity * intensity / 25.5);

  /**
   * Startet eine Berechnung. opts: { res, shadows, ambient, atlas, sky (Anteil Himmelslicht der Sonne), bounce, flipSun }.
   * job.step(ms) rechnet höchstens ms Millisekunden weiter, job.done zeigt das Ende an, job.result hat dann die Daten.
   */
  function createJob(doc: Doc, options?: Partial<LightOptions>): LightJob {
    const opts: LightOptions = Object.assign({ res: 16, shadows: true, ambient: 0, atlas: 2048, sky: 0.2, bounce: false, flipSun: false }, options || {});
    const lights = collect(doc, opts.flipSun), occ = opts.shadows ? buildOccluders(doc) : null;
    const faces = collectFaces(doc);
    const A = opts.atlas;
    const rs = layout(faces, opts.res, A);
    if (rs < 0) throw new Error(_('light.e_big'));
    const data = new Uint8ClampedArray(A * A * 4).fill(255);
    let total = 0;
    faces.forEach((f) => { total += f.w * f.h; });
    const hasEnv = lights.some((l) => l.type === 'env') && opts.sky > 0;
    const passes = opts.bounce ? 2 : 1;
    const job: LightJob = {
      lights: lights, faceCount: faces.length, luxels: total, res: rs, atlas: A, done: false, result: null, doneLux: 0, t0: 0,
      progress: () => (total ? Math.min(1, job.doneLux / (total * passes)) : 1),
      step: () => false
    };
    const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const amb = opts.ambient, fl: LightSource[] = [];
    // Himmelsproben: kosinusgewichtet, Vogel-Scheibe
    const SK = 6, skyLocal: Vec3[] = [];
    for (let k = 0; k < SK; k++) { const r = Math.sqrt((k + 0.5) / SK), ph = k * 2.399963; skyLocal.push([r * Math.cos(ph), r * Math.sin(ph), Math.sqrt(1 - r * r)]); }
    const envs = lights.filter((l): l is Extract<LightSource, { type: 'env' }> => l.type === 'env');

    function shade(px: number, py: number, pz: number, n: Vec3, f: LightFace, c: number[]): void {
      let r = amb, g = amb, b = amb;
      for (let k = 0; k < fl.length; k++) {
        const L = fl[k];
        if (L.type === 'env') {
          const cos = -(n[0] * L.dir[0] + n[1] * L.dir[1] + n[2] * L.dir[2]);
          if (cos <= 0) continue;
          const v = L.i / 255 * cos * LIGHTSCALE;
          if (occ && occ.hit(px, py, pz, -L.dir[0], -L.dir[1], -L.dir[2], 12000)) continue;
          r += v * L.col[0]; g += v * L.col[1]; b += v * L.col[2];
          continue;
        }
        const dx = L.p[0] - px, dy = L.p[1] - py, dz = L.p[2] - pz, d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < 1) continue;
        const d = Math.sqrt(d2), ix = dx / d, iy = dy / d, iz = dz / d;
        const cos = n[0] * ix + n[1] * iy + n[2] * iz;
        if (cos <= 0) continue;
        let v = KC * L.i * L.i * L.i / d2 * cos / 255;
        if (v < 0.0004) continue;
        if (L.type === 'spot') {
          const ca = -(ix * L.dir[0] + iy * L.dir[1] + iz * L.dir[2]);
          if (ca <= L.cout) continue;
          if (ca < L.cin) v *= (ca - L.cout) / (L.cin - L.cout);
        }
        if (occ && occ.hit(px, py, pz, ix, iy, iz, d - 1)) continue;
        r += v * L.col[0]; g += v * L.col[1]; b += v * L.col[2];
      }
      if (hasEnv && n[2] > -0.95) {
        let vis = 0;
        for (let k = 0; k < SK; k++) {
          const q = skyLocal[k], dx = f.U[0] * q[0] + f.V[0] * q[1] + n[0] * q[2], dy = f.U[1] * q[0] + f.V[1] * q[1] + n[1] * q[2], dz = f.U[2] * q[0] + f.V[2] * q[1] + n[2] * q[2];
          if (dz < 0.05) continue;
          if (occ && occ.hit(px, py, pz, dx, dy, dz, 12000)) continue;
          vis++;
        }
        if (vis) for (let e = 0; e < envs.length; e++) { const L = envs[e], v = L.di / 255 * opts.sky * vis / SK; r += v * L.dcol[0]; g += v * L.dcol[1]; b += v * L.dcol[2]; }
      }
      c[0] = r; c[1] = g; c[2] = b;
    }
    const out = (x: number): number => Math.round(Math.pow(Math.min(1, Math.max(0, x)) * 255 / 256, GAMMA) * 256);
    const tmp = [0, 0, 0];
    const lum = (r: number, g: number, b: number): number => 0.3 * r + 0.59 * g + 0.11 * b;

    function lightsFor(f: LightFace): void {
      fl.length = 0;
      lights.forEach((L) => {
        if (L.type === 'env') { if (-(f.n[0] * L.dir[0] + f.n[1] * L.dir[1] + f.n[2] * L.dir[2]) > 0 || (hasEnv && f.n[2] > -0.95)) fl.push(L); } else if (f.n[0] * L.p[0] + f.n[1] * L.p[1] + f.n[2] * L.p[2] - f.d > 0.01) fl.push(L);
      });
    }
    const pos = (f: LightFace, s: number, t: number): Vec3 => [f.n[0] * (f.d + 0.5) + f.U[0] * s + f.V[0] * t, f.n[1] * (f.d + 0.5) + f.U[1] * s + f.V[1] * t, f.n[2] * (f.d + 0.5) + f.U[2] * s + f.V[2] * t];

    // Patches für den Bounce: grobe Zellen der direkt beleuchteten Flächen als Sekundärlichter
    let patches: Patch[] = [];
    function buildPatches() {
      const all: Patch[] = [];
      faces.forEach((f) => {
        const cs = Math.max(1, Math.round(48 / f.rs));
        for (let cy = 0; cy < f.h; cy += cs) for (let cx = 0; cx < f.w; cx += cs) {
          let r = 0, g = 0, b = 0, n = 0;
          for (let y = cy; y < Math.min(f.h, cy + cs); y++) for (let x = cx; x < Math.min(f.w, cx + cs); x++) { const o = (y * f.w + x) * 3; r += f.rad![o]; g += f.rad![o + 1]; b += f.rad![o + 2]; n++; }
          r = Math.max(0, r / n - amb); g = Math.max(0, g / n - amb); b = Math.max(0, b / n - amb);
          const L = lum(r, g, b), area = n * f.rs * f.rs;
          if (L < 0.03) continue;
          const p = pos(f, f.s0 + (cx + Math.min(cs, f.w - cx) / 2) * f.rs, f.t0 + (cy + Math.min(cs, f.h - cy) / 2) * f.rs);
          all.push({ x: p[0] + f.n[0] * 0.5, y: p[1] + f.n[1] * 0.5, z: p[2] + f.n[2] * 0.5, nx: f.n[0], ny: f.n[1], nz: f.n[2], r: r * 0.45, g: g * 0.45, b: b * 0.45, a: area, w: L * area });
        }
      });
      all.sort((a, b) => b.w - a.w);
      patches = all.slice(0, 900);
    }
    function faceCandidates(f: LightFace): Patch[] {
      const c = pos(f, (f.s0 + f.s1) / 2, (f.t0 + f.t1) / 2), rad = Math.hypot(f.s1 - f.s0, f.t1 - f.t0) / 2;
      const list: { q: Patch; s: number }[] = [];
      patches.forEach((q) => {
        const dx = c[0] - q.x, dy = c[1] - q.y, dz = c[2] - q.z, d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d > 600 + rad) return;
        if (f.n[0] * q.x + f.n[1] * q.y + f.n[2] * q.z - f.d < -rad * 0.2) return; // Patch hinter der Fläche
        if (q.nx * dx + q.ny * dy + q.nz * dz < -rad) return; // Fläche hinter dem Patch
        list.push({ q: q, s: q.w / Math.max(d * d, 900) });
      });
      list.sort((a, b) => b.s - a.s);
      return list.slice(0, 24).map((x) => x.q);
    }

    let phase = 0, fi = 0, j = 0, cur: LightFace | null = null, cand: Patch[] = [];
    function writeFace(f: LightFace): void {
      for (let y = 0; y < f.h; y++) for (let x = 0; x < f.w; x++) {
        const o = (y * f.w + x) * 3, a = ((f.y0 + 1 + y) * A + f.x0 + 1 + x) * 4;
        data[a] = out(f.rad![o]); data[a + 1] = out(f.rad![o + 1]); data[a + 2] = out(f.rad![o + 2]); data[a + 3] = 255;
      }
      const set = (x: number, y: number, sx: number, sy: number): void => { const a = ((f.y0 + y) * A + f.x0 + x) * 4, b = ((f.y0 + sy) * A + f.x0 + sx) * 4; data[a] = data[b]; data[a + 1] = data[b + 1]; data[a + 2] = data[b + 2]; data[a + 3] = 255; };
      for (let y = 1; y <= f.h; y++) { set(0, y, 1, y); set(f.W - 1, y, f.W - 2, y); }
      for (let x = 0; x < f.W; x++) { set(x, 0, x, 1); set(x, f.H - 1, x, f.H - 2); }
      f.rad = null;
    }
    let dark = 0;

    job.step = function (ms: number): boolean {
      const t0 = now();
      if (!job.t0) job.t0 = t0;
      let guard = 0;
      for (;;) {
        if (phase === 0 || phase === 2) {
          if (fi >= faces.length) {
            if (phase === 0 && opts.bounce) { buildPatches(); phase = 2; fi = 0; cur = null; continue; }
            phase = 3; fi = 0; continue;
          }
          const f = faces[fi];
          if (!cur) {
            cur = f; j = 0;
            if (phase === 0) { f.rad = new Float32Array(f.w * f.h * 3); lightsFor(f); f.maxd = 0; } else cand = faceCandidates(f);
          }
          for (; j < f.h; j++) {
            for (let i = 0; i < f.w; i++) {
              const p = pos(f, f.s0 + i * f.rs, f.t0 + j * f.rs), o = (j * f.w + i) * 3;
              if (phase === 0) {
                shade(p[0], p[1], p[2], f.n, f, tmp);
                f.rad![o] = tmp[0]; f.rad![o + 1] = tmp[1]; f.rad![o + 2] = tmp[2];
                const l = lum(tmp[0], tmp[1], tmp[2]) - amb; if (l > f.maxd) f.maxd = l;
              } else {
                let ar = 0, ag = 0, ab = 0;
                for (let k = 0; k < cand.length; k++) {
                  const q = cand[k], dx = q.x - p[0], dy = q.y - p[1], dz = q.z - p[2], d2 = dx * dx + dy * dy + dz * dz;
                  if (d2 < 4) continue;
                  const d = Math.sqrt(d2), cl = (f.n[0] * dx + f.n[1] * dy + f.n[2] * dz) / d, cp = -(q.nx * dx + q.ny * dy + q.nz * dz) / d;
                  if (cl <= 0 || cp <= 0) continue;
                  const F = q.a * cl * cp / (Math.PI * (d2 + 400));
                  if (F * Math.max(q.r, q.g, q.b) < 0.002) continue;
                  if (occ && occ.hit(p[0], p[1], p[2], dx / d, dy / d, dz / d, d - 1.5)) continue;
                  ar += q.r * F; ag += q.g * F; ab += q.b * F;
                }
                f.rad![o] += ar; f.rad![o + 1] += ag; f.rad![o + 2] += ab;
              }
            }
            job.doneLux += f.w;
            if ((++guard & 3) === 0 && now() - t0 > ms) return false;
          }
          if (phase === 0 && f.maxd < 0.01) dark++;
          cur = null; fi++;
          if (now() - t0 > ms) return false;
        } else if (phase === 3) {
          while (fi < faces.length) {
            writeFace(faces[fi++]);
            if ((fi & 63) === 0 && now() - t0 > ms) return false;
          }
          return finish();
        }
      }
    };
    function finish() {
      if (job.done) return true;
      const map = new WeakMap();
      faces.forEach((f) => {
        let arr = map.get(f.b);
        if (!arr) { arr = []; map.set(f.b, arr); }
        arr[f.fi] = { x0: f.x0, y0: f.y0, rs: f.rs, s0: f.s0, t0: f.t0, U: f.U, V: f.V };
      });
      job.result = { W: A, H: A, data: data, map: map, lights: lights.length, luxels: total, res: rs, faces: faces.length, dark: dark, ms: now() - job.t0, bounce: !!opts.bounce };
      job.done = true;
      return true;
    }
    return job;
  }

  /** UV im Atlas für einen Weltpunkt p auf einer berechneten Fläche (Zeile 0 = oben, ohne Spiegelung). */
  function uvFor(info: FaceUv, p: Vec3, W: number, H: number): [number, number] {
    const s = p[0] * info.U[0] + p[1] * info.U[1] + p[2] * info.U[2], t = p[0] * info.V[0] + p[1] * info.V[1] + p[2] * info.V[2];
    return [(info.x0 + 1 + (s - info.s0) / info.rs + 0.5) / W, (info.y0 + 1 + (t - info.t0) / info.rs + 0.5) / H];
  }
  /** Mitte des weißen Blocks (unbeleuchtete Flächen). */
  const whiteUV = (W: number, H: number): [number, number] => [2 / W, 2 / H];

  function runAll(doc: Doc, opts?: Partial<LightOptions>): LightResult {
    const job = createJob(doc, opts);
    while (!job.step(1000)) { /* weiter */ }
    return job.result as LightResult;
  }

  return { range: range, collect: collect, createJob: createJob, runAll: runAll, uvFor: uvFor, whiteUV: whiteUV, buildOccluders: buildOccluders, collectFaces: collectFaces };
})(Core);
export default Light;
